import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ActionBar, Checkbox, Input, IssueIcon, SpinnerWithBackground, Text } from '@viapeople/components'
import { useEffect, useMemo, useState } from 'react'
import { type Control, Controller, type FieldValues, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type { CycleRolePermission, CycleRolesPermissionsSearchInput } from '../types'

enum PermissionTemplateFixedRoleType {
	RATER = 'rater',
	UNSOLICITED = 'unsolicited',
	PARTICIPANT = 'participant',
	PRIMARY_MANAGER = 'primary_manager',
	CO_MANAGER = 'co_manager',
	SECONDARY_MANAGER = 'secondary_manager',
	HIGH_LEVEL_MANAGER = 'high_level_manager',
	DIRECT_REPORTS = 'direct_reports',
	PEER = 'peer',
	DOWN = 'down',
	UP = 'up',
}

const FIXED_ROLE_DISPLAY_NAMES: Record<PermissionTemplateFixedRoleType, string> = {
	[PermissionTemplateFixedRoleType.RATER]: 'Rater',
	[PermissionTemplateFixedRoleType.UNSOLICITED]: 'Unsolicited',
	[PermissionTemplateFixedRoleType.PARTICIPANT]: 'Self',
	[PermissionTemplateFixedRoleType.PRIMARY_MANAGER]: 'Direct Manager',
	[PermissionTemplateFixedRoleType.CO_MANAGER]: 'Co-Manager',
	[PermissionTemplateFixedRoleType.SECONDARY_MANAGER]: 'Secondary Manager',
	[PermissionTemplateFixedRoleType.HIGH_LEVEL_MANAGER]: 'High Level Manager',
	[PermissionTemplateFixedRoleType.DIRECT_REPORTS]: 'Direct Reports',
	[PermissionTemplateFixedRoleType.PEER]: 'Peer',
	[PermissionTemplateFixedRoleType.DOWN]: 'Downward',
	[PermissionTemplateFixedRoleType.UP]: 'Upward',
}

const initialFixedRolesData = {
	calibration: false,
	reportAccess: false,
	pdfs: false,
	maxDeclinesAllowed: 0,
	roleId: null as string | null,
	id: '',
	name: null as string | null,
	displayName: null as string | null,
	description: null as string | null,
}

const defaultFixedRoles: CycleRolePermission[] = [
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.PARTICIPANT,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.UNSOLICITED,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.RATER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.PRIMARY_MANAGER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.CO_MANAGER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.SECONDARY_MANAGER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.HIGH_LEVEL_MANAGER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.DIRECT_REPORTS,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.PEER,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.DOWN,
	},
	{
		...initialFixedRolesData,
		fixedRole: PermissionTemplateFixedRoleType.UP,
	},
]

type FormValues = {
	roles: CycleRolePermission[]
	fixedRoles: CycleRolePermission[]
}

const PermissionCheckbox = ({
	control,
	name,
	defaultValue = false,
	className = '',
}: {
	control: unknown
	name: string
	defaultValue?: boolean
	className?: string
}) => (
	<Controller
		control={control as Control<FieldValues>}
		name={name}
		defaultValue={defaultValue}
		render={({ field }) => (
			<Checkbox
				className={className}
				checked={field.value || false}
				onChange={field.onChange}
				onBlur={field.onBlur}
				name={field.name}
			/>
		)}
	/>
)

const MaxDeclinesInput = ({
	control,
	name,
	defaultValue = 0,
}: {
	control: unknown
	name: string
	defaultValue?: number
}) => (
	<Controller
		control={control as Control<FieldValues>}
		name={name}
		defaultValue={defaultValue}
		render={({ field }) => (
			<Input
				className='h-10 m-0'
				type='number'
				min={0}
				value={field.value || 0}
				onChange={field.onChange}
				onBlur={field.onBlur}
				name={field.name}
			/>
		)}
	/>
)

export function CycleRolesPermissionsTab() {
	const { organizationId = '', cycleId = '' } = useParams<{
		organizationId: string
		cycleId: string
	}>()
	const queryClient = useQueryClient()
	const [tableData, setTableData] = useState<CycleRolePermission[]>([])
	const toast = useToast()
	const { data: cycle, isLoading: isCycleLoading } = useQuery({
		...trpc.admin.cycles.getById.queryOptions({ id: cycleId }),
		enabled: !!cycleId,
	})

	const { data: rolesPermissions } = useQuery({
		...trpc.admin.cycles.roles.permissions.list.queryOptions({
			perPage: 99999,
			organizationId,
			cycleId: cycle?.id || '',
		}),
		enabled: !!cycleId,
	})

	const normalizedFixedRoles = useMemo<CycleRolePermission[]>(
		() =>
			rolesPermissions?.data?.fixedRoles && rolesPermissions.data.fixedRoles.length > 0
				? rolesPermissions.data.fixedRoles
				: defaultFixedRoles,
		[rolesPermissions?.data?.fixedRoles],
	)

	const normalizedRoles = useMemo<CycleRolePermission[]>(
		() => (rolesPermissions?.data?.roles || []).filter(Boolean) as CycleRolePermission[],
		[rolesPermissions?.data?.roles],
	)

	const editRolesPermissionsMutation = useMutation({
		...trpc.admin.cycles.roles.permissions.edit.mutationOptions(),
		onSuccess: () => {
			toast.showToast('Roles permissions updated successfully')
		},
		onError: () => {
			toast.showToast('Failed to update roles permissions', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.roles.permissions.list.queryKey({
					cycleId: cycle?.id || '',
					organizationId,
				}),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
		defaultValues: {
			roles: [],
			fixedRoles: [],
		},
	})

	const watchedFixedRoles = watch('fixedRoles')
	const watchedRoles = watch('roles')

	useEffect(() => {
		if (normalizedFixedRoles.length > 0) {
			setValue('fixedRoles', normalizedFixedRoles)
		}

		if (normalizedRoles.length > 0) {
			setValue('roles', normalizedRoles)
		}
	}, [normalizedFixedRoles, normalizedRoles, setValue])

	useEffect(() => {
		const fixedRoles = (watchedFixedRoles || []).filter(Boolean)
		const roles = (watchedRoles || []).filter(Boolean)
		setTableData([...fixedRoles, ...roles])
	}, [watchedFixedRoles, watchedRoles])

	const getFieldInfo = (rolePermission: CycleRolePermission) => {
		const isFixedRole = !!rolePermission.fixedRole
		const fieldArrayName = isFixedRole ? 'fixedRoles' : 'roles'
		const arrayIndex = isFixedRole
			? normalizedFixedRoles.findIndex((r) => r.fixedRole === rolePermission.fixedRole)
			: normalizedRoles.findIndex((r) => r.id === rolePermission.id)

		return { fieldArrayName, arrayIndex, isFixedRole }
	}

	const handleFormSubmit = (data: FormValues) => {
		const prepareRoles = (roles: CycleRolePermission[]) => {
			return roles.filter(Boolean).map((role) => ({
				...role,
				id: role.id || undefined,
				maxDeclinesAllowed: Number(role.maxDeclinesAllowed || 0),
			}))
		}

		const roles = prepareRoles(data.roles)

		const fixedRoles = prepareRoles(data.fixedRoles)

		editRolesPermissionsMutation.mutate({
			cycleId: cycle?.id || '',
			roles,
			fixesRoles: fixedRoles,
		})
	}

	const columns: Column<CycleRolePermission>[] = [
		{
			field: 'displayName',
			label: 'Role',
			colSpan: 2,
			sortable: false,
			render: (rolePermission: CycleRolePermission) => {
				const { isFixedRole } = getFieldInfo(rolePermission)
				return (
					<Text className='capitalize'>
						{isFixedRole
							? FIXED_ROLE_DISPLAY_NAMES[rolePermission.fixedRole as PermissionTemplateFixedRoleType] ||
								rolePermission.fixedRole
							: rolePermission.name}
					</Text>
				)
			},
		},
		{
			field: 'calibration',
			label: 'Calibration',
			colSpan: 1,
			sortable: false,
			render: (rolePermission: CycleRolePermission, index?: number) => {
				if (index === undefined) return null

				const { fieldArrayName, arrayIndex } = getFieldInfo(rolePermission)
				if (arrayIndex === -1) return null

				return (
					<PermissionCheckbox
						control={control}
						name={`${fieldArrayName}.${arrayIndex}.calibration`}
						defaultValue={rolePermission.calibration || false}
						className='ml-[5px]'
					/>
				)
			},
		},
		{
			field: 'reportAccess',
			label: 'Report Access',
			colSpan: 1,
			sortable: false,
			render: (rolePermission: CycleRolePermission, index?: number) => {
				if (index === undefined) return null

				const { fieldArrayName, arrayIndex } = getFieldInfo(rolePermission)
				if (arrayIndex === -1) return null

				return (
					<PermissionCheckbox
						control={control}
						name={`${fieldArrayName}.${arrayIndex}.reportAccess`}
						defaultValue={rolePermission.reportAccess || false}
						className='ml-[4px]'
					/>
				)
			},
		},
		{
			field: 'pdfs',
			label: 'PDFs',
			colSpan: 1,
			sortable: false,
			render: (rolePermission: CycleRolePermission, index?: number) => {
				if (index === undefined) return null

				const { fieldArrayName, arrayIndex } = getFieldInfo(rolePermission)
				if (arrayIndex === -1) return null

				return (
					<PermissionCheckbox
						control={control}
						name={`${fieldArrayName}.${arrayIndex}.pdfs`}
						defaultValue={rolePermission.pdfs || false}
						className='ml-[3px]'
					/>
				)
			},
		},
		{
			field: 'maxDeclinesAllowed',
			label: 'Max Declines Allowed',
			sortable: false,
			render: (rolePermission: CycleRolePermission, index?: number) => {
				if (index === undefined) return null

				const { fieldArrayName, arrayIndex } = getFieldInfo(rolePermission)
				if (arrayIndex === -1) return null

				return (
					<MaxDeclinesInput
						control={control}
						name={`${fieldArrayName}.${arrayIndex}.maxDeclinesAllowed`}
						defaultValue={rolePermission.maxDeclinesAllowed || 0}
					/>
				)
			},
		},
	]

	if (isCycleLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	return (
		<div className='mt-6 relative'>
			<div className='pb-24 bg-neutral-0 rounded-lg'>
				<GenericTable<CycleRolePermission, CycleRolesPermissionsSearchInput>
					showSearch={false}
					showPagination={false}
					data={{
						data: tableData,
					}}
					columns={columns}
					emptyState={{
						title: 'No cycle roles permissions added yet',
						description: 'Get started by adding cycle roles permissions',
						icon: IssueIcon,
					}}
				/>
			</div>
			<ActionBar
				rightActions={[
					{
						label: 'Save',
						variant: 'primary',
						onClick: handleSubmit(handleFormSubmit),
						isLoading: editRolesPermissionsMutation.isPending,
					},
				]}
			/>
		</div>
	)
}
